import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { aribaPasswordAdapterChoices } from '~/api/evisync';
import { SYNC_SYMBOL } from '~/components/Admin/IntegrationPage/constants';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { CopyButton } from '~/eds/src/components/Button/subcomponents/CopyButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { AribaSyncType } from '~/enums/AribaSyncType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';

import { InfoDataType, SyncPairConfigInfo } from './types';

export const useConfigurationInfos = ({
  id: syncPairId,
  providerOptions,
  createdBy,
  evisortFolderPath,
  allowedSyncFileTypes,
  dateCreated,
  dateUpdated,
  provider,
}: SyncPairConfigInfo) => {
  const clientId = useClientId();
  const intl = useIntl();
  const formatDate = useFormatDate();
  const hasLocalization = useFlag(FlagType.LanguageSelect);

  interface aribaField {
    fieldName?: string;
  }

  interface evisortField {
    label?: string;
  }

  interface mappingField {
    sync: AribaSyncType;
    meta: {
      aribaField?: aribaField;
      evisortField?: evisortField;
    };
  }

  const noFieldAvailable = intl.formatMessage({
    id: 'UseConfigurationInfos.noFieldAvailable',
    defaultMessage: 'No field available',
    description: 'Fallback message for no field available',
  });

  const { data: userCreate } = api.endpoints.resolveUser.useQuery({
    id: createdBy,
    params: { clientId, includeDeleted: true },
  });

  const { data: userUpdate } = api.endpoints.resolveUser.useQuery({
    id: providerOptions.updatedBy ?? createdBy,
    params: { clientId, includeDeleted: true },
  });

  const userCreateInfo = useMemo(() => {
    if (!userCreate) {
      return {
        firstName: '',
        lastName: '',
        email: '',
        id: '',
      };
    }

    const { firstName, lastName, email, id } = userCreate;
    return { firstName, lastName, email, id };
  }, [userCreate]);

  const userUpdateInfo = useMemo(() => {
    if (!userUpdate) {
      return userCreateInfo;
    }
    const { firstName, lastName, email, id } = userUpdate;
    return { firstName, lastName, email, id };
  }, [userUpdate]);

  const enableAribaOnetimeSyncInfo = providerOptions.syncType === 'one_time';

  const { mapping, importFile } = providerOptions;

  const FieldMapping = () => {
    if (mapping?.length === 0) {
      return (
        <Text>
          {intl.formatMessage({
            id: 'UseConfigurationInfos.noFieldMappings',
            defaultMessage: 'No field mappings',
            description: 'Fallback message for no field mappings',
          })}
        </Text>
      );
    }
    return (
      <Layout direction="column" preset="m">
        {mapping?.map((field: mappingField) => {
          const syncDirection = SYNC_SYMBOL[field.sync];

          const { aribaField = {}, evisortField = {} } = field.meta || {};

          const fieldName = aribaField?.fieldName ?? noFieldAvailable;
          const label = evisortField?.label ?? noFieldAvailable;

          return (
            <Layout align="space-between">
              <Text>{[label, syncDirection, fieldName].join(' ')}</Text>
            </Layout>
          );
        })}
      </Layout>
    );
  };

  const chips = allowedSyncFileTypes.map((fileType: string) => ({
    text: fileType,
  }));

  const oneTimeAndCsvSync = [
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.aribaDocuments',
        defaultMessage: 'Ariba Documents',
        description: 'Title for Ariba documents section',
      }),
      type: InfoDataType.FILESYSTEM,
      props: {
        file: {
          name:
            importFile ??
            intl.formatMessage({
              id: 'UseConfigurationInfos.noAvailableFiles',
              defaultMessage: 'no available files',
              description: 'Fallback message for no available files',
            }),
        },
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.dateCreated1',
        defaultMessage: 'Date Created',
        description: 'Title for date created section',
      }),
      type: InfoDataType.USER_ACTIVITY,
      props: {
        activity: intl.formatMessage({
          id: 'UseConfigurationInfos.dateCreated2',
          defaultMessage: 'Date Created',
          description: 'Activity name for date created',
        }),
        date: new Date(dateCreated),
        user: {
          id: userCreateInfo.id,
          firstName: userCreateInfo.firstName,
          lastName: userCreateInfo.lastName,
          email: userCreateInfo.email,
        },
        format: 'short_datetime',
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.dateUpdated1',
        defaultMessage: 'Date Updated',
        description: 'Title for date updated section',
      }),
      type: InfoDataType.USER_ACTIVITY,
      props: {
        activity: intl.formatMessage({
          id: 'UseConfigurationInfos.dateUpdated2',
          defaultMessage: 'Date Updated',
          description: 'Activity name for date updated',
        }),
        date: new Date(dateUpdated),
        user: {
          id: userUpdateInfo.id,
          firstName: userUpdateInfo.firstName,
          lastName: userUpdateInfo.lastName,
          email: userUpdateInfo.email,
        },
        format: 'short_datetime',
      },
    },
  ];

  const continuousAribaSync = [
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.fileTypes',
        defaultMessage: 'File Types',
        description: 'Title for file types section',
      }),
      type: InfoDataType.CHIPS,
      props: {
        chips,
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.dateCreated3',
        defaultMessage: 'Date Created',
        description: 'Title for date created section',
      }),
      type: InfoDataType.USER_ACTIVITY,
      props: {
        activity: intl.formatMessage({
          id: 'UseConfigurationInfos.dateCreated4',
          defaultMessage: 'Date Created',
          description: 'Activity name for date created',
        }),
        date: new Date(dateCreated),
        user: {
          id: userCreateInfo.id,
          firstName: userCreateInfo.firstName,
          lastName: userCreateInfo.lastName,
          email: userCreateInfo.email,
        },
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.dateUpdated3',
        defaultMessage: 'Date Updated',
        description: 'Title for date updated section',
      }),
      type: InfoDataType.USER_ACTIVITY,
      props: {
        activity: intl.formatMessage({
          id: 'UseConfigurationInfos.dateUpdated4',
          defaultMessage: 'Date Updated',
          description: 'Activity name for date updated',
        }),
        date: new Date(dateUpdated),
        user: {
          id: userUpdateInfo.id,
          firstName: userUpdateInfo.firstName,
          lastName: userUpdateInfo.lastName,
          email: userUpdateInfo.email,
        },
      },
    },
  ];

  const extraContextNarrowedType = providerOptions.extraContext
    ? (providerOptions.extraContext as {
        taskDiscoveryDetails?: {
          enabled: boolean;
          approver?: {
            id: string;
            passwordAdapter: string;
          };
          discoveryStartDate?: Date;
        };
      })
    : { taskDiscoveryDetails: { enabled: false } };

  const taskDiscoveryInfo = [
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.useAribaTask',
        defaultMessage: 'Use Ariba Task for Faster Data Sync',
        description: 'Title for Ariba task usage section',
      }),
      type: InfoDataType.TEXT,
      props: {
        text: extraContextNarrowedType.taskDiscoveryDetails?.enabled
          ? intl.formatMessage({
              id: 'UseConfigurationInfos.yes',
              defaultMessage: 'Yes',
              description: 'Yes value for boolean fields',
            })
          : intl.formatMessage({
              id: 'UseConfigurationInfos.no',
              defaultMessage: 'No',
              description: 'No value for boolean fields',
            }),
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.discoveryStartDate',
        defaultMessage: 'Date Filter',
        description: 'Title for Ariba date filter section',
      }),
      type: InfoDataType.TEXT,
      props: {
        text: extraContextNarrowedType.taskDiscoveryDetails?.discoveryStartDate
          ? formatDate(
              new Date(
                extraContextNarrowedType.taskDiscoveryDetails
                  ?.discoveryStartDate,
              ),
              hasLocalization ? 'iso' : 'short',
            )
          : '--',
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.aribaTaskUser',
        defaultMessage: 'Ariba Task User',
        description: 'Title for Ariba task user section',
      }),
      type: InfoDataType.TEXT,
      props: {
        text:
          extraContextNarrowedType.taskDiscoveryDetails?.approver?.id ?? '--',
      },
    },
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.aribaTaskUserAuthMethod',
        defaultMessage: 'Ariba Task User Authentication Method',
        description: 'Title for Ariba task user authentication method section',
      }),
      type: InfoDataType.TEXT,
      props: {
        text:
          aribaPasswordAdapterChoices
            .filter(
              (choice) =>
                choice.value ===
                extraContextNarrowedType.taskDiscoveryDetails?.approver
                  ?.passwordAdapter,
            )
            .map((choice) => choice.label)[0] ?? '--',
      },
    },
  ];

  const enableFieldMapping = [
    {
      title: intl.formatMessage({
        id: 'UseConfigurationInfos.listOfFieldMappings',
        defaultMessage: 'List of Field Mappings (Evisort - Ariba)',
        description: 'Title for field mappings section',
      }),
      type: InfoDataType.CONTENT,
      props: {
        children: <FieldMapping />,
      },
    },
  ];

  const infos = useMemo(() => {
    return [
      {
        title: intl.formatMessage({
          id: 'UseConfigurationInfos.provider',
          defaultMessage: 'Provider',
          description: 'Title for provider section',
        }),
        type: InfoDataType.CONTENT,
        props: {
          children: (
            <Layout spacing={2} align="center">
              <ProviderLogo provider={provider} size="s" />
              <Text>{provider}</Text>
            </Layout>
          ),
        },
      },
      {
        title: intl.formatMessage({
          id: 'UseConfigurationInfos.realmId',
          defaultMessage: 'Realm ID',
          description: 'Title for realm ID section',
        }),
        type: InfoDataType.TEXT,
        props: {
          text: providerOptions.realm,
        },
      },
      {
        title: intl.formatMessage({
          id: 'UseConfigurationInfos.suvId',
          defaultMessage: 'SUV ID',
          description: 'Title for SUV ID section',
        }),
        type: InfoDataType.TEXT,
        props: {
          text: providerOptions.location,
        },
      },
      {
        title: intl.formatMessage({
          id: 'UseConfigurationInfos.evisortFolder',
          defaultMessage: 'Evisort Folder',
          description: 'Title for Evisort folder section',
        }),
        type: InfoDataType.FILESYSTEM,
        props: {
          folder: { name: evisortFolderPath, path: evisortFolderPath },
        },
      },
      {
        title: intl.formatMessage({
          id: 'UseConfigurationInfos.syncPairId',
          defaultMessage: 'Sync Pair ID',
          description: 'Title for sync pair ID section',
        }),
        type: InfoDataType.CONTENT,
        props: {
          children: (
            <Layout spacing={2} align="center">
              <Text>{syncPairId}</Text>
              <CopyButton copyText={syncPairId} size="s" />
            </Layout>
          ),
        },
      },
      ...(enableAribaOnetimeSyncInfo ? oneTimeAndCsvSync : continuousAribaSync),
      ...taskDiscoveryInfo,
      ...enableFieldMapping,
    ];
  }, [
    providerOptions,
    createdBy,
    evisortFolderPath,
    allowedSyncFileTypes,
    dateCreated,
    dateUpdated,
    userCreateInfo,
    userUpdateInfo,
  ]);

  return infos;
};
